import config from "config"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"

/**
 * Instruction from mui doc
 * ==============================
 * Ref: https://mui.com/material-ui/customization/theming/
 * Theme composition: using theme options to define other options
 * When the value for a theme option is dependent on another theme option, you should compose the theme in steps.
 */

//TODO:: custom venue horizontal kiosk style later
export const venueKioskHUIThemeOptionFactory = (theme) => ({
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          userSelect:
            config("env") === "production" &&
            ["kiosk-v", "kiosk-h"].includes(config("device"))
              ? "none"
              : "auto",
        },
        "@keyframes pulse": {
          "0%": {
            opacity: 1,
          },
          "50%": {
            opacity: 0.6,
          },
          "100%": {
            opacity: 1,
          },
        },
        "@keyframes ripple": {
          from: {
            opacity: 1,
            transform: "scale3d(0.75,0.75,1)",
          },
          to: {
            opacity: 0,
            transform: "scale3d(1.6,1.6,1)",
          },
        },
        "@keyframes slideUp": {
          from: {
            transform: "translateY(100%)",
          },
          to: {
            transform: "translateY(0%)",
          },
        },
        "@keyframes maptalksfadeIn": {
          from: {
            opacity: 0,
          },

          to: {
            opacity: 1,
          },
        },
        "@keyframes highlightTag": {
          "100%": {
            backgroundPosition: "-100% 0",
          },
        },
        ".mtk-occupant-text-marker": {},
        /**
         * "mtk-ui-fadein" is the build class name for handling UIComponent fade-in animation in maptalks.js.
         * [@Soruce](https://github.com/maptalks/maptalks.js/blob/2a4ab207619543237a2943bbb1c293863c6a98bd/src/ui/UIComponent.ts#L201)
         * [@Style](https://github.com/maptalks/maptalks.js/blob/2a4ab207619543237a2943bbb1c293863c6a98bd/assets/maptalks.css#L202)
         */
        ".mtk-ui-fadein": {
          animation: "maptalksfadeIn 0.5s",
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        rounded: {
          borderRadius: 12,
        },
      },
      variants: [
        {
          props: { variant: "logo" },
          style: {
            borderRadius: 8,
            width: 55,
            height: 55,
            marginRight: theme.spacing(2.25),
            "& .MuiAvatar-img": {
              objectFit: "contain",
            },
          },
        },
        {
          props: { variant: "circular-logo" },
          style: {
            borderRadius: "50%",
            width: 80,
            height: 80,
            marginRight: theme.spacing(2.25),
            "& .MuiAvatar-img": {
              objectFit: "cover",
            },
          },
        },
        {
          props: { color: "primary" },
          style: {
            background: theme.palette.primary.main,
            borderRadius: 5,
            "& .MuiSvgIcon-root": {
              width: 20,
            },
          },
        },
      ],
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "uppercase",
          lineHeight: 1.45,
          borderRadius: "8px",
          boxShadow: "none",
        },
        startIcon: {
          marginRight: theme.spacing(1.25),
        },
        endIcon: {
          marginLeft: theme.spacing(1.25),
        },
        sizeSmall: {
          fontSize: 18,
          padding: theme.spacing(1, 2),
        },
        sizeMedium: {
          fontSize: 20,
          padding: theme.spacing(2, 2.5),
        },
        sizeLarge: {
          fontSize: 24,
          padding: theme.spacing(2, 5),
        },
        outlinedSizeLarge: {
          borderWidth: "2px",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderBottomWidth: theme.spacing(0.25),
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          whiteSpace: "pre",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      },
      variants: [
        {
          props: { variant: "medium" },
          style: {
            "& .MuiListItemText-primary": {
              fontSize: theme.typography.body2.fontSize,
            },
            "& .MuiListItemText-secondary": {
              fontSize: theme.typography.caption.fontSize,
            },
          },
        },
        {
          props: { color: "primary" },
          style: {
            "& .MuiListItemText-primary": {
              color: theme.palette.primary.main,
            },
          },
        },
      ],
    },
    MuiRadio: {
      defaultProps: {
        icon: <CheckCircleIcon />,
        checkedIcon: <CheckCircleIcon />,
      },
    },
    MuiFab: {
      styleOverrides: {
        default: {
          background: "#fff",
        },
        sizeLarge: {
          padding: theme.spacing(5, 6),
          "& .MuiSvgIcon-root": {
            width: 40,
            height: 40,
          },
        },
      },
      variants: [
        {
          props: { variant: "rounded" },
          style: {
            borderRadius: 12,
          },
        },
      ],
    },
    MuiIconButton: {
      styleOverrides: {
        colorPrimary: {
          background: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          "&:hover": {
            background: theme.palette.primary.dark,
          },
          "& .MuiSvgIcon-root": {
            "& path": { fill: theme.palette.primary.contrastText },
          },
        },
      },
      variants: [
        {
          props: { variant: "contained" },
          style: {
            borderRadius: 5,
          },
        },
      ],
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: { gap: theme.spacing(1.25), paddingRight: theme.spacing(2) },
        grouped: {
          "&:not(:last-of-type)": {
            borderRadius: "32px",
          },
          "&:not(:first-of-type)": {
            marginLeft: 1,
            borderLeft: `1px solid rgba(203, 203, 203, 0.5)`,
            borderRadius: "32px",
          },
        },
      },
      variants: [
        {
          props: { variant: "borderSeperate" },
          style: {
            "& .MuiToggleButtonGroup-grouped:not(:first-of-type)": {
              borderLeft: `1px solid transparent`,
            },
          },
        },
      ],
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          color: theme.palette.primary.main,
          borderColor: "rgba(203, 203, 203, 0.5)",
          "&.Mui-selected, &.Mui-selected:hover": {
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.primary.main,
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        gutterBottom: {
          marginBottom: theme.spacing(2),
        },
      },
    },
    /**
     * VenueUI (Vui) - Horizontal Kiosk
     * Extra Component for venue.in.th projects
     */
    VuiDescriptionList: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        },
        labelText: {
          fontWeight: 700,
          fontSize: "0.875rem",
          textTransform: "uppercase",
        },
        contentText: {
          fontSize: "1rem",
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          overflow: "hidden",
          wordBreak: "break-all",
          fontWeight: 300,
        },
      },
    },
    VuiSearchToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: `9999px`,
          background: theme.palette.background.paper,
          width: "100%",
          padding: theme.spacing(1.5, 3),
          justifyContent: "flex-start",
          border: `1px solid #ececec`,
        },
        searchIcon: {
          color: theme.palette.text.secondary,
          fontSize: "1.875rem",
          marginRight: theme.spacing(2.5),
        },
        label: {
          color: theme.palette.text.secondary,
          fontSize: "1.5rem",
        },
      },
    },
    VuiNavigationControl: {
      styleOverrides: {
        wrapper: {
          height: 48,
          display: "flex",
          flexDirection: "row",
          width: "100%",
          padding: theme.spacing(0, 4),
          gap: theme.spacing(3),
        },
        navigationAction: {
          minWidth: 73,
          width: 73,
          borderRadius: "8px",
          padding: theme.spacing(1.5, 3),
          "& .MuiBottomNavigationAction-label": {
            display: "none",
          },
          "& .MuiSvgIcon-root": {
            width: 24,
            height: 24,
            marginBottom: 0,
            color: theme.palette.primary.main,
          },
          border: `1px solid ${theme.palette.primary.main}`,
        },
        languageButton: {
          "& .MuiTypography-root": {
            display: "none",
          },
        },
      },
    },
    VuiSearchBasePopup: {
      styleOverrides: {
        dialog: {
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          "& .MuiBackdrop-root": {
            position: "absolute",
            "&:after": {
              position: "fixed",
              display: "flex",
              content: "''",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            },
          },
        },
        dialogPaper: {
          maxWidth: "1320px",
          background: "transparent",
          boxShadow: "none",
          alignSelf: "center",
          maxHeight: "calc(100% - 24px)",
          margin: theme.spacing(5),
        },
        dialogContent: {
          display: "flex",
          padding: 0,
          flexDirection: "column",
          // Apply a className to each section for ordering.
          "& .VuiSearchResult-root": {
            order: 0,
            marginBottom: theme.spacing(1.5),
          },
          "& .VuiSearchPopupContent-searchBar": {
            order: 1,
            marginBottom: theme.spacing(1.5),
          },
          "& .VuiSearchKeyboard-root": {
            order: 9999,
          },
          "& .VuiSearchPopupContent-categoryFilterBar": {
            order: 2,
            marginBottom: theme.spacing(1.5),
          },
          "& .VuiSearchPopupContent-closeButton": {
            zIndex: 9999,
            position: "absolute",
            top: theme.spacing(1.5),
            right: theme.spacing(1.5),
          },
          "& .VuiSearchPopupContent-closeButton--bottom": {
            display: "none",
          },
          "& .VuiSearchPopupContent-venueFilterContainer": {
            display: "none",
          },
        },
        dialogBackdrop: {},
      },
    },
    VuiSearchResult: {
      styleOverrides: {
        root: {
          flexGrow: 1,
          width: "100%",
          overflow: "hidden",
        },
        rootDialog: {
          position: "relative",
          backgroundColor: theme.palette.background.paper,
          // TODO: Update the height to 413px when adding category quick search.
          height: "600px",
          borderRadius: "20px",
          padding: theme.spacing(3, 5, 0),
          "&:after": {
            display: "flex",
            pointerEvents: "none",
            content: "''",
            width: "100%",
            height: "147px",
            position: "absolute",
            borderRadius: "20px",
            left: 0,
            right: 0,
            bottom: 0.5,
            background: `linear-gradient(0deg, ${theme.palette.common.white} 9.97%, transparent 72.39%)`,
          },
        },
        list: {
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          margin: "0 auto",
          gap: theme.spacing(1.5),
          paddingTop: 0,
          paddingBottom: theme.spacing(6),
        },
        listItem: {
          margin: "0 auto",
          padding: 0,
          width: "100%",
        },
      },
    },
    VuiSearchResultItem: {
      styleOverrides: {
        chip: {
          display: "none",
          borderRadius: "4px",
          height: "18px",
          fontWeight: 400,
          backgroundColor: theme.palette.grey[400],
          color: theme.palette.common.white,
          fontSize: "0.625rem",
          "& .MuiChip-label": {
            padding: theme.spacing(0, 1),
          },
        },
        primaryWrapper: {
          display: "flex",
          alignItems: "flex-start",
          gap: theme.spacing(0.75),
        },
      },
    },
    VuiNoSearchResult: {
      styleOverrides: {
        root: {
          display: "flex",
          height: "300px",
          justifyContent: "center",
          alignItems: "center",
        },
        noResultText: {},
      },
    },
    VuiSearchPopupContent: {
      styleOverrides: {
        root: {
          display: "flex",
          width: "auto",
          gap: theme.spacing(2.5),
          alignItems: "center",
          color: theme.palette.text.primary,
        },
        dialogPaper: {
          alignSelf: "center",
        },
        closeButton: {
          padding: theme.spacing(2),
          color: theme.palette.text.primary,
        },
        closeButtonIcon: {
          width: "0.75rem",
          height: "0.75rem",
          color: "inherit",
        },
        searchInput: {
          fontSize: "1.5rem",
          gap: theme.spacing(2),
          borderRadius: "9999px",
          padding: theme.spacing(1, 3),
          backgroundColor: theme.palette.common.white,
          fontWeight: 500,
          height: "64px",
          "& .MuiInputAdornment-root": {
            "& .MuiSvgIcon-root": {
              color: "#cccccc",
            },
            "&.MuiInputAdornment-positionEnd": {
              "& .MuiSvgIcon-root": {
                width: 10,
                height: 10,
                color: theme.palette.text.primary,
              },
            },
          },
          "& input": {
            padding: 0,
          },
          "& .MuiSvgIcon-root": {
            fontSize: "2rem",
          },
        },
        suggestedList: {},
      },
    },
    VuiAdSuggestList: {
      defaultProps: {
        suggestBrandCarouselOptions: {
          perPage: 9,
          gap: "1rem",
        },
      },
      styleOverrides: {
        root: {
          padding: theme.spacing(1.5),
          backgroundColor: theme.palette.background.paper,
          borderRadius: "8px",
        },
        suggestTitle: {
          fontSize: "1.25rem",
          fontWeight: 700,
        },
        suggestList: {
          "& .splide__track": {
            margin: 0,
            padding: theme.spacing(2, 0, 0.5),
          },
        },
        suggestListItem: {
          display: "flex",
          flexDirection: "column",
          padding: 0,
          gap: theme.spacing(1),
          justifyContent: "center",
          alignItems: "center",
        },
        suggestItemAvatar: {
          width: "110px",
          height: "110px",
          margin: 0,
          "& .MuiSvgIcon-root": {
            width: "32px",
            height: "32px",
          },
        },
        suggestItemContent: {
          marginTop: theme.spacing(0.5),
          height: "40px",
          "& .MuiTypography-root": {
            fontSize: "1rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            textAlign: "center",
          },
        },
      },
    },
    VuiSearchKeyboard: {
      styleOverrides: {
        root: {
          padding: theme.spacing(4),
          borderRadius: "20px",
          backgroundColor: theme.palette.background.paper,
        },
      },
    },
    VuiDetailCard: {
      styleOverrides: {
        card: {},
        image: {
          width: "50%",
          margin: "0 auto",
          marginBottom: theme.spacing(3),
          borderRadius: "10px",
          [theme.breakpoints.up("sm")]: {
            width: "50%",
            margin: "0 auto",
          },
        },
        infoListItem: {},
        infoListItemWithNavigation: {
          padding: 0,
          "& .MuiListItemButton-root": {
            padding: 0,
          },
        },
        cardHeaderAction: {},
        cardHeaderActionButton: {},
        cardHeader: {
          padding: 0,
          paddingBottom: theme.spacing(2),
          "& .MuiCardHeader-title": {
            fontSize: "20px",
            fontWeight: 700,
          },
          "& .MuiCardHeader-action": {
            marginRight: 0,
          },
        },
        cardContent: {
          padding: 0,
          paddingTop: theme.spacing(1),
        },
        cardContentPrimaryText: {
          color: theme.palette.text.primary,
          fontWeight: 700,
        },
        cardContentSecondaryText: {
          color: theme.palette.text.secondary,
        },
      },
    },
    VuiSearchQuickLink: {
      styleOverrides: {
        root: {
          pb: 1,
          mb: 1,
          mx: -2,
          px: 2,
        },
        stack: {
          pb: 2,
        },
        item: {
          boxShadow: "rgb(0 0 0 / 10%) 0px 2px 10px",
          border: "none",
          background: "white",
          "& .MuiSvgIcon-root": {
            width: "16px",
            height: "16px",
          },
        },
      },
    },
    VuiOnscreenKeyboard: {
      styleOverrides: {
        root: {
          // [Example] (https://hodgef.com/simple-keyboard/editor/?d=hodgef/react-simple-keyboard-demos/tree/uc-customization)
          // Add your custom style here
          "& .hg-theme-default": {
            fontFamily: "inherit",
          },
          "& .simple-keyboard": {
            maxWidth: "100%",
            padding: theme.spacing(1),
            borderRadius: "20px",
            backgroundColor: "transparent",
            "& .hg-rows": {
              width: "604px",
              margin: "0 auto",
            },
            "&.hg-theme-default .hg-row": {
              marginBottom: theme.spacing(0.5),
              "&:last-child": {
                marginBottom: 0,
              },
              "& .hg-button:not(:last-child)": {
                marginRight: theme.spacing(1.375),
              },
            },
            "& .hg-button": {
              height: 46,
              width: 50,
              backgroundColor: theme.palette.common.black,
              color: theme.palette.common.white,
              fontSize: "1rem",
              fontWeight: 500,
              borderRadius: "6px",
              "&>span": {
                display: "flex",
                fontSize: "inherit",
              },
              "&.hg-standardBtn": {
                width: 50,
              },
            },
            "& .hg-activeButton": {
              backgroundColor: theme.palette.grey[300],
              color: theme.palette.text.primary,
            },
            "& .hg-button-number,& .hg-button-symbol, & .hg-button-abc,& .hg-button-lang":
              {
                fontSize: "12px",
                flexGrow: 1,
              },
            "& .hg-button-lang, & .hg-button-number, & .hg-button-abc": {
              flexGrow: 0.5,
            },
            "& .hg-button-shift,& .hg-button-bksp": {
              fontSize: "20px",
            },
            "& .hg-button-shift": {
              fontSize: "20px",
              flexGrow: 2,
              flexShrink: 0,
            },
            "& .hg-button-enter": {
              flexGrow: 2,
            },
            "& .hg-button-space": {
              flexGrow: 8,
            },
            "& .hg-primary-button": {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
            "& .hg-secondary-button": {
              backgroundColor: theme.palette.text.secondary,
              color: theme.palette.primary.contrastText,
            },
            "& .hg-small-button": {},
          },
        },
      },
    },
    VuiFilterVenueRadioButtons: {
      styleOverrides: {
        root: {
          padding: theme.spacing(1),
          width: "100%",
          gap: theme.spacing(5),
          justifyContent: "center",
          textTransform: "uppercase",
          "& .MuiFormControlLabel-label": {
            display: "flex",
          },
        },
        formControlLabel: {},
        labelImage: {
          height: "32px",
        },
      },
    },
    VuiCarousel: {
      styleOverrides: {
        root: {
          "& .splide__pagination__page": {
            background: theme.palette.grey[500],
          },
          "& .splide__pagination__page.is-active": {
            background: theme.palette.primary.main,
          },
          "& .splide__pagination": {
            position: "static",
          },
          "& .splide__track": {
            overflow: "hidden",
            margin: theme.spacing(-3),
            padding: theme.spacing(3),
          },
          "& .splide__arrow": {
            background: "none",
            margin: theme.spacing(0, -5),
            "& svg": {
              fill: theme.palette.grey[500],
            },
          },
          "& .splide:not(.is-overflow) .splide__pagination": {
            display: "flex",
            "&>li:last-of-type:first-of-type": {
              visibility: "hidden",
            },
          },
          "& .splide:not(.is-overflow):not(:has(li:only-child)) .splide__pagination":
            {
              visibility: "visible", // pagination is visible when there's more than 1 slide in carousel
            },
        },
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 15,
      },
      styleOverrides: {
        root: {
          borderRadius: "0",
          color: "#000000",
        },
      },
      variants: [
        {
          props: { variant: "image" },
          style: {
            "& .MuiCardHeader-root": {
              "& .MuiCardHeader-subheader": {
                display: "flex",
                width: "100%",
              },
              "& .MuiCardHeader-subheader .sub-category-text": {
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            },
            "& .MuiCardHeader-content": {
              overflow: "hidden",
            },
            "& .MuiCardHeader-title": {
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              lineHeight: 1,
            },
            "& .MuiCardHeader-root .MuiCardHeader-avatar": {
              height: "100%",
            },
            "& .MuiCardActionArea-root": {
              width: "100%",
              height: "100%",
            },
          },
        },
        {
          props: { variant: "full-image" },
          style: {
            borderRadius: "10px",
            "& .MuiCardActionArea-root:after": {
              content: '""',
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
              height: "100%",
              background:
                "linear-gradient(180deg, rgba(0, 0, 0, 0.10) 31.48%, rgba(0, 0, 0, 0.84) 95.03%)",
            },
            "& .MuiCardMedia-root": {
              height: "140px",
            },
            "& .MuiCardHeader-root": {
              position: "absolute",
              padding: theme.spacing(0, 2, 1),
              minHeight: "60px",
              width: "100%",
              bottom: "0px",
              zIndex: 1,
              "& .MuiCardHeader-content": {
                color: "white",
              },
              "& .MuiCardHeader-title": {
                fontSize: "1rem",
                lineHeight: 1,
              },
              "& .MuiAvatar-root": {
                borderRadius: "8px",
                width: "45px",
                height: "45px",
              },
              "& .MuiCardHeader-avatar": {
                marginRight: theme.spacing(2),
              },
              "& .MuiCardHeader-subheader": {
                fontSize: "0.875rem",
                color: "white",
              },
            },
          },
        },
      ],
    },
    HorizontalScrollPane: {
      styleOverrides: {
        root: {
          width: "100%",
          "& .simplebar-track.simplebar-horizontal": {
            display: "none",
          },
        },
        listWrapper: {
          width: "100%",
          padding: theme.spacing(1),
        },
      },
    },
    LoadingScreen: {
      styleOverrides: {
        root: {
          position: "fixed",
          width: "100dvw",
          height: "100dvh",
          backgroundColor: theme.palette.common.white,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 10000,
        },
        logo: {
          maxWidth: "70dvw",
        },
      },
    },
    FeatureChipToggleBar: {
      styleOverrides: {
        root: {
          padding: 0,
          borderRadius: 0,
          overflowX: "scroll",
          //hide scrollbar
          "::-webkit-scrollbar": {
            display: "none",
          },
          MsOverflowStyle: "none",
          scrollbarWidth: "none",
        },
        button: {
          padding: theme.spacing(1.5, 3),
          borderRadius: "8px",
          boxShadow: "none",
          background: theme.palette.common.white,
          border: `1px solid ${theme.palette.grey[200]}`,
          whiteSpace: "nowrap",
          height: "48px",
          color: theme.palette.text.secondary,
          fontWeight: 700,
          "&.MuiToggleButtonGroup-grouped:not(:last-of-type)": {
            borderRadius: "32px",
            margin: 0,
          },
          "&.MuiToggleButtonGroup-grouped:not(:first-of-type)": {
            border: `1px solid ${theme.palette.grey[200]}`,
            margin: 0,
            borderRadius: "32px",
          },
          "&.Mui-selected": {
            "& svg": {
              fill: theme.palette.primary.contrastText,
            },
          },
          ":hover": {
            background: theme.palette.common.white,
          },
          "& svg": {
            width: 24,
            height: 24,
            fill: theme.palette.text.secondary,
          },
        },
        text: {
          fontSize: "1rem",
          textTransform: "uppercase",
          padding: theme.spacing(0, 1.5),
          letterSpacing: "0.00938em",
        },
      },
    },
    VuiAdModal: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        },
        closeButton: {
          padding: theme.spacing(0.5, 2),
          marginTop: theme.spacing(2),
        },
      },
    },
    VuiAdImageCarousel: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        },
        carousel: {
          "& .splide__pagination": {
            marginTop: theme.spacing(0.5),
          },
          "& .splide__pagination__page": {
            width: "8px",
            height: "8px",
          },
          "& .splide__pagination__page.is-active": {
            backgroundColor: theme.palette.primary.main,
            transition: `width ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}, borderRadius ${theme.transitions.duration.standard}s ${theme.transitions.easing.easeInOut}`,
            transform: "none",
            width: "24px",
            borderRadius: "30px",
          },
        },
        carouselItem: {
          display: "flex",
          justifyContent: "center",
        },
        image: {
          width: "320px",
          aspectRatio: "3 / 4",
          objectFit: "cover",
          borderRadius: "16px",
        },
      },
    },
    VuiAnnoucementCard: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(1),
        },
        closeButtonContainer: {
          display: "flex",
          justifyContent: "flex-end",
        },
        closeButton: {
          backgroundColor: "rgba(255, 255, 255, 0.50)",
          borderRadius: "50%",
          padding: 0,
          minWidth: "24px",
          "& .MuiSvgIcon-root": {
            width: "50%",
          },
          cursor: "pointer",
          "&.MuiButtonBase-root:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.50)",
          },
        },
        detailCard: {
          display: "flex",
          padding: theme.spacing(2),
          gap: theme.spacing(1),
          borderRadius: "16px",
        },
        cardContent: {
          padding: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          "&.MuiCardContent-root:last-child": {
            paddingBottom: 0,
          },
        },
        cardMedia: {
          aspectRatio: "1/1",
          width: "30%",
          borderRadius: "8px",
          objectFit: "cover",
        },
        titleContainer: {
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          color: theme.palette.primary.main,
        },
        locationContainer: {
          display: "flex",
          gap: theme.spacing(1),
        },
        iconWrapper: {
          width: "16px",
          height: "16px",
          "& .MuiSvgIcon-root": {
            height: "100%",
            width: "100%",
          },
        },
      },
    },
  },
})
